import React, { useState, useEffect } from 'react';
import { FileText, ShieldCheck, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * Modal de exibição e assinatura eletrônica do Contrato de Prestação de Serviços.
 * Registra: nome do representante, CPF, IP, user-agent, hash SHA-256 e timestamp.
 */
const ContratoEntregador = ({ empresaId, isOpen, onClose, onAssinado }) => {
  const [conteudo, setConteudo] = useState('');
  const [versao, setVersao] = useState('');
  const [loading, setLoading] = useState(false);
  const [carregandoTemplate, setCarregandoTemplate] = useState(false);
  
  // Form de assinatura
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [aceite, setAceite] = useState(false);
  const [scrollAtBottom, setScrollAtBottom] = useState(false);
  
  useEffect(() => {
    if (!isOpen || !empresaId) return;
    setCarregandoTemplate(true);
    setScrollAtBottom(false);
    api.get(`/contratos/template/${empresaId}`)
      .then(res => {
        setConteudo(res.data.conteudo || '');
        setVersao(res.data.versao || '');
      })
      .catch(() => {
        toast.error('Erro ao carregar contrato');
      })
      .finally(() => setCarregandoTemplate(false));
  }, [isOpen, empresaId]);
  
  // Se o conteúdo já cabe na tela (sem necessidade de scroll), libera assinatura imediatamente
  const contratoRefCallback = (el) => {
    if (el && el.scrollHeight <= el.clientHeight + 5) {
      setScrollAtBottom(true);
    }
  };
  
  const formatarCpf = (valor) => {
    const limpo = valor.replace(/\D/g, '').slice(0, 11);
    return limpo
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };
  
  const podeAssinar = aceite && nome.trim().length >= 3 && cpf.replace(/\D/g, '').length === 11 && scrollAtBottom;
  
  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
      setScrollAtBottom(true);
    }
  };
  
  const handleAssinar = async () => {
    if (!podeAssinar) {
      if (!scrollAtBottom) toast.error('Leia o contrato até o final antes de assinar');
      else toast.error('Preencha todos os campos e aceite os termos');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/contratos/assinar/${empresaId}`, {
        nome_representante: nome.trim(),
        cpf_representante: cpf.replace(/\D/g, ''),
        aceite_termos: true,
      });
      toast.success('Contrato assinado com sucesso!');
      if (onAssinado) onAssinado(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao assinar contrato');
    } finally {
      setLoading(false);
    }
  };
  
  // Renderiza o markdown simples (negrito **texto**)
  const renderConteudo = (texto) => {
    return texto.split('\n').map((linha, i) => {
      const partes = linha.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className={linha.startsWith('**CLÁUSULA') ? 'mt-4 mb-2 font-bold text-orange-700' : 'mb-1 text-gray-700 leading-relaxed'}>
          {partes.map((p, j) => 
            p.startsWith('**') && p.endsWith('**') 
              ? <strong key={j}>{p.slice(2, -2)}</strong>
              : p
          )}
        </p>
      );
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="contrato-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-6 h-6 text-orange-600" />
            Contrato de Prestação de Serviços
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Versão {versao} · Leia atentamente todas as cláusulas antes de assinar
          </p>
        </DialogHeader>
        
        {carregandoTemplate ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Corpo do contrato com scroll */}
            <div
              ref={contratoRefCallback}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto bg-gray-50 border border-gray-200 rounded-xl p-6 my-2"
              style={{ maxHeight: '50vh' }}
              data-testid="contrato-conteudo"
            >
              {renderConteudo(conteudo)}
            </div>
            
            {/* Indicador de leitura */}
            {!scrollAtBottom && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2" data-testid="aviso-leitura">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm text-yellow-900">
                  Role o contrato até o final para liberar a assinatura
                </span>
              </div>
            )}
            {scrollAtBottom && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2" data-testid="leitura-concluida">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-900">
                  Leitura concluída. Você pode prosseguir com a assinatura.
                </span>
              </div>
            )}
            
            {/* Form de assinatura */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3 mt-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-orange-600" />
                Assinatura Eletrônica
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Nome do Representante Legal <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Nome completo"
                    data-testid="input-nome-representante"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    CPF do Representante <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={cpf}
                    onChange={(e) => setCpf(formatarCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    data-testid="input-cpf-representante"
                    maxLength={14}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={aceite}
                  onCheckedChange={setAceite}
                  data-testid="checkbox-aceite"
                  disabled={loading}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  Declaro que <strong>li integralmente</strong> e <strong>aceito todos os termos e cláusulas</strong> do contrato acima.
                  Esta assinatura tem validade jurídica conforme a MP 2.200-2/2001 e o Código Civil Brasileiro.
                  Os dados de IP, dispositivo e horário desta assinatura serão registrados.
                </span>
              </label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
                Cancelar
              </Button>
              <Button
                onClick={handleAssinar}
                disabled={!podeAssinar || loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                data-testid="btn-assinar-contrato"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registrando...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Assinar Eletronicamente
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContratoEntregador;
